import { useState } from "react";
import {
  CanAccess,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { ExternalLink, Pause, Play, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

import type {
  Agency,
  AgencyBrandProfile,
  AgencyMember,
  AgencyPortalUser,
  BrandExtraction,
  Sale,
} from "../types";
import { displayPhone } from "../featureCohorts/normalizePhone";
import {
  AGENCIES_SORT,
  PORTAL_USERS_SORT,
} from "../echoflicks/useStudioDirectory";
import { AddAgencyMemberDialog } from "./AddAgencyMemberDialog";
import { AgencyBillingSection } from "./AgencyBillingSection";
import { useAllRecords } from "./allRecords";
import { BrandProfileDialog } from "./BrandProfileDialog";
import { CreateAgencyDialog } from "./CreateAgencyDialog";
import { FilmsByCreatorSection } from "./FilmsByCreatorSection";
import { InvitePortalUserDialog } from "./InvitePortalUserDialog";
import { PendingSignupsSection } from "./PendingSignupsSection";
import { pendingSignups } from "./pendingSignups";
import { effectivePoweredBy, safeParseBrandProfile } from "./brandProfile";
import {
  agencyInternalState,
  handedOverLabel,
  INTERNAL_SWITCH_TITLE,
  internalToggleData,
  internalToggleMessage,
  isHiddenInternalAgency,
  isInternalAgency,
  PROSPECT_BADGE_LABEL,
  PROSPECT_BADGE_TITLE,
  visibleAgencies,
} from "./internalAgencies";
import {
  clientUsersFirst,
  isShadowPortalUser,
  isStaffPortalUser,
  staffSignInEmails,
  staffSignInPattern,
} from "./staffShadowAccounts";

/**
 * Agencies — real-estate B2B tenants: members (WhatsApp numbers), portal
 * users (external logins of the agency portal) and brand guidelines (the
 * profile the video pipeline consumes via the bot's envelope).
 *
 * Membership implies the `real_estate` feature cohort via a DB trigger, so
 * adding a number here also gives it the 🏠 Real Estate occasion. Admin-only
 * (same CanAccess rule as Feature Cohorts).
 */
export const AgenciesPage = () => (
  <CanAccess resource="agencies" action="list">
    <AgenciesView />
  </CanAccess>
);

const AgenciesView = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  // PT-100 / B4.3: our own test studios (marked internal by a person) are
  // hidden by default; prospect studios stay in view with a badge (plan G4).
  const [hideInternal, setHideInternal] = useState(true);

  // B6.3: every page, not the first 100 agencies / 1,000 logins and members.
  const {
    data: agenciesData,
    error: agenciesError,
    isPending: agenciesPending,
  } = useAllRecords<Agency>("agencies", { sort: AGENCIES_SORT });
  const { data: members } = useAllRecords<AgencyMember>("agency_members", {
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: portalUsers } = useAllRecords<AgencyPortalUser>(
    "agency_portal_users",
    { sort: PORTAL_USERS_SORT },
  );
  const { data: brandProfiles } = useAllRecords<AgencyBrandProfile>(
    "agency_brand_profiles",
    { sort: { field: "id", order: "ASC" } },
  );
  const { data: extractions } = useGetList<BrandExtraction>(
    "brand_extractions",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  // REF-143: the staff roster, so a pending signup from a staff address
  // hides with "Hide internal" (isStaffEmail mirrors is_staff_portal_email).
  const { data: roster } = useGetList<Sale>(
    "sales",
    { pagination: { page: 1, perPage: 500 } },
    { staleTime: 5 * 60 * 1000 },
  );
  const [deleteOne] = useDelete();
  const [update] = useUpdate();

  if (agenciesError) {
    return <SetupNotice />;
  }
  if (agenciesPending) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading agencies…</div>
    );
  }

  const allAgencies = agenciesData ?? [];
  const agencies = visibleAgencies(allAgencies, hideInternal);
  const internalCount = allAgencies.filter(isHiddenInternalAgency).length;
  const selected =
    agencies.find((agency) => String(agency.id) === selectedId) ?? agencies[0];
  const membersFor = (agencyId: Agency["id"]) =>
    (members ?? []).filter((member) => member.agency_id === agencyId);
  const usersFor = (agencyId: Agency["id"]) =>
    (portalUsers ?? []).filter((user) => user.agency_id === agencyId);
  const profileFor = (agencyId: Agency["id"]) =>
    (brandProfiles ?? []).find((profile) => profile.agency_id === agencyId);
  const pending = pendingSignups(portalUsers ?? [], {
    hideInternal,
    roster: roster ?? [],
  });
  const extractionsFor = (agencyId: Agency["id"]) =>
    (extractions ?? [])
      .filter((extraction) => extraction.agency_id === agencyId)
      .slice(0, 5);

  const removeMember = (member: AgencyMember) => {
    if (
      !window.confirm(
        `Remove ${displayPhone(member.phone_digits)} from ${selected?.name}? ` +
          "Their videos stop carrying the agency brand, and the implied Real Estate " +
          "cohort grant is removed (explicit cohort grants are kept).",
      )
    ) {
      return;
    }
    deleteOne(
      "agency_members",
      { id: member.id, previousData: member },
      {
        onSuccess: () => {
          notify("Member removed — the bot picks this up within a minute.", {
            type: "info",
          });
          refresh();
        },
        onError: () =>
          notify("Could not remove the member — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  const removePortalUser = (user: AgencyPortalUser) => {
    if (
      !window.confirm(
        `Remove portal access for ${user.email}? They can no longer sign in to the agency portal.` +
          (isShadowPortalUser(user)
            ? " This automatic staff login comes back whenever staff access is granted again; to remove someone everywhere, revoke their staff access instead."
            : ""),
      )
    ) {
      return;
    }
    deleteOne(
      "agency_portal_users",
      { id: user.id, previousData: user },
      {
        onSuccess: () => {
          notify("Portal user removed.", { type: "info" });
          refresh();
        },
        onError: () =>
          notify("Could not remove the portal user — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  const toggleStatus = (agency: Agency) => {
    const next = agency.status === "active" ? "paused" : "active";
    update(
      "agencies",
      { id: agency.id, data: { status: next }, previousData: agency },
      {
        onSuccess: () => {
          notify(
            next === "paused"
              ? "Agency paused — members keep the occasion but echoes render unbranded."
              : "Agency resumed — branding is live again.",
            { type: "info" },
          );
          refresh();
        },
        onError: () =>
          notify("Could not update the agency — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  const toggleBillingMode = (agency: Agency) => {
    const next = agency.billing_mode === "standard" ? "exempt" : "standard";
    update(
      "agencies",
      { id: agency.id, data: { billing_mode: next }, previousData: agency },
      {
        onSuccess: () => {
          notify(
            next === "standard"
              ? "Agency is now metered — studio echoes use wallet credits."
              : "Agency is now on the Premiere plan — unmetered studio echoes.",
            { type: "info" },
          );
          refresh();
        },
        onError: () =>
          notify("Could not update billing mode — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  const toggleInternal = (agency: Agency) => {
    const next = !isInternalAgency(agency);
    update(
      "agencies",
      {
        id: agency.id,
        // B4.3: a person's choice is "manual" and never flipped by the DB.
        data: internalToggleData(agency, next),
        previousData: agency,
      },
      {
        onSuccess: () => {
          notify(internalToggleMessage(next), { type: "info" });
          refresh();
        },
        onError: () =>
          notify(
            "Could not update the internal flag — are migrations 20260924120000_agencies_is_internal and 20260926120000_agency_internal_auto applied?",
            { type: "warning" },
          ),
      },
    );
  };

  const deleteAgency = (agency: Agency) => {
    if (
      !window.confirm(
        `Delete ${agency.name}? Members, portal users, the brand profile and ` +
          "extraction history are deleted with it (implied cohort grants are removed too).",
      )
    ) {
      return;
    }
    deleteOne(
      "agencies",
      { id: agency.id, previousData: agency },
      {
        onSuccess: () => {
          notify("Agency deleted.", { type: "info" });
          setSelectedId(null);
          refresh();
        },
        onError: () =>
          notify("Could not delete the agency — please try again.", {
            type: "warning",
          }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Agencies</h1>
          <p className="text-sm text-muted-foreground">
            Real-estate tenants: WhatsApp members, portal logins and brand
            guidelines. Members get the 🏠 Real Estate occasion automatically;
            their echoes carry the agency brand once a profile is saved.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="hide-internal-agencies"
              checked={hideInternal}
              onCheckedChange={setHideInternal}
            />
            <Label
              htmlFor="hide-internal-agencies"
              className="text-xs font-normal text-muted-foreground"
            >
              Hide internal{internalCount > 0 ? ` (${internalCount})` : ""}
            </Label>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> New agency
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {agencies.map((agency) => {
          const isSelected = selected?.id === agency.id;
          return (
            <Card
              key={agency.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(String(agency.id))}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setSelectedId(String(agency.id));
                }
              }}
              className={`w-64 cursor-pointer transition-colors ${
                isSelected
                  ? "border-primary"
                  : "hover:border-muted-foreground/40"
              }`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  {agency.name}
                  <Badge variant="secondary">
                    {membersFor(agency.id).length}
                  </Badge>
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  {agency.slug}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1 pt-0">
                <InternalStateBadges agency={agency} compact />
                {agency.status === "paused" && (
                  <Badge variant="destructive" className="text-xs">
                    paused
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {agency.billing_mode === "standard" ? "metered" : "premiere"}
                </Badge>
                {agency.staff_alias_suffix && (
                  <Badge
                    variant="outline"
                    className="font-mono text-xs"
                    title={`Staff sign-in: ${staffSignInPattern(agency.staff_alias_suffix)}`}
                  >
                    +{agency.staff_alias_suffix}
                  </Badge>
                )}
                {profileFor(agency.id) ? (
                  <Badge variant="outline" className="text-xs">
                    brand v{profileFor(agency.id)?.version}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    no brand yet
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
        {agencies.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {allAgencies.length > 0
              ? "Only our own internal studios exist — turn off “Hide internal” to see them."
              : "No agencies yet — create the first tenant with “New agency”."}
          </p>
        )}
      </div>

      <PendingSignupsSection
        users={pending.visible}
        hiddenInternal={pending.hiddenInternal}
      />

      {selected && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                {selected.name}
                <InternalStateBadges agency={selected} />
                {selected.status === "paused" && (
                  <Badge variant="destructive">paused</Badge>
                )}
                <Badge variant="secondary">
                  {selected.billing_mode === "standard"
                    ? "metered"
                    : "premiere"}
                </Badge>
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <span className="font-mono">{selected.slug}</span>
                {selected.website_url && (
                  <a
                    href={selected.website_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
                  >
                    {selected.website_url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-1">
              <CanAccess resource="agencies" action="edit" record={selected}>
                <div className="mr-2 flex items-center gap-2">
                  <Switch
                    id="agency-is-internal"
                    checked={isInternalAgency(selected)}
                    onCheckedChange={() => toggleInternal(selected)}
                  />
                  <Label
                    htmlFor="agency-is-internal"
                    className="text-xs font-normal text-muted-foreground"
                    title={INTERNAL_SWITCH_TITLE}
                  >
                    Internal
                  </Label>
                </div>
              </CanAccess>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleBillingMode(selected)}
              >
                {selected.billing_mode === "standard"
                  ? "Make Premiere"
                  : "Make metered"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleStatus(selected)}
              >
                {selected.status === "active" ? (
                  <>
                    <Pause className="mr-1 h-4 w-4" /> Pause branding
                  </>
                ) : (
                  <>
                    <Play className="mr-1 h-4 w-4" /> Resume
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete agency"
                onClick={() => deleteAgency(selected)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <MembersSection
              members={membersFor(selected.id)}
              onAdd={() => setAddMemberOpen(true)}
              onRemove={removeMember}
            />
            <Separator />
            <PortalUsersSection
              users={usersFor(selected.id)}
              staffSuffix={selected.staff_alias_suffix}
              onInvite={() => setInviteOpen(true)}
              onRemove={removePortalUser}
            />
            <Separator />
            <AgencyBillingSection key={selected.id} agency={selected} />
            <Separator />
            <FilmsByCreatorSection
              agency={selected}
              portalUsers={portalUsers ?? []}
            />
            <Separator />
            <BrandSection
              profile={profileFor(selected.id)}
              onEdit={() => setBrandOpen(true)}
            />
            <ExtractionsSection extractions={extractionsFor(selected.id)} />
          </CardContent>
        </Card>
      )}

      <CreateAgencyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => {
          setSelectedId(id);
          refresh();
        }}
      />
      {selected && (
        <AddAgencyMemberDialog
          agency={selected}
          open={addMemberOpen}
          onOpenChange={setAddMemberOpen}
          onCreated={() => refresh()}
        />
      )}
      {selected && (
        <InvitePortalUserDialog
          agency={selected}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          onCreated={() => refresh()}
        />
      )}
      {selected && (
        <BrandProfileDialog
          agency={selected}
          profile={profileFor(selected.id)}
          open={brandOpen}
          onOpenChange={setBrandOpen}
          onSaved={() => refresh()}
        />
      )}
    </div>
  );
};

/**
 * B4.3: what the internal flag means for this studio. A prospect (set up by
 * staff, internal until its client signs in) keeps a visible badge; a studio a
 * person marked internal says "Internal"; a handed-over studio shows when.
 */
const InternalStateBadges = ({
  agency,
  compact,
}: {
  agency: Agency;
  compact?: boolean;
}) => {
  const state = agencyInternalState(agency);
  const handedOver = handedOverLabel(agency);
  return (
    <>
      {state === "internal" && (
        <Badge
          variant="outline"
          className="text-xs"
          title={INTERNAL_SWITCH_TITLE}
        >
          Internal
        </Badge>
      )}
      {state === "prospect" && (
        <Badge
          variant="outline"
          className="text-xs"
          title={PROSPECT_BADGE_TITLE}
        >
          {PROSPECT_BADGE_LABEL}
        </Badge>
      )}
      {handedOver && (
        <Badge variant="secondary" className="text-xs" title={handedOver}>
          {compact ? "Handed over" : handedOver}
        </Badge>
      )}
    </>
  );
};

const MembersSection = ({
  members,
  onAdd,
  onRemove,
}: {
  members: AgencyMember[];
  onAdd: () => void;
  onRemove: (member: AgencyMember) => void;
}) => (
  <section>
    <div className="mb-1 flex items-center justify-between">
      <h3 className="text-sm font-medium">WhatsApp members</h3>
      <Button size="sm" variant="outline" onClick={onAdd}>
        <Plus className="mr-1 h-4 w-4" /> Add member
      </Button>
    </div>
    <p className="mb-2 text-xs text-muted-foreground">
      A number belongs to at most one agency (video attribution must be
      unambiguous). Membership implies the Real Estate cohort.
    </p>
    {members.length === 0 ? (
      <p className="text-sm text-muted-foreground">No members yet.</p>
    ) : (
      <ul className="flex flex-col">
        {members.map((member) => (
          <li
            key={member.id}
            className="flex items-center gap-2 border-b py-2 text-sm last:border-b-0"
          >
            <span className="font-mono">
              {displayPhone(member.phone_digits)}
            </span>
            {member.display_name && (
              <span className="truncate text-muted-foreground">
                {member.display_name}
              </span>
            )}
            <span className="ml-auto flex items-center gap-1">
              {member.source !== "crm" && (
                <Badge variant="outline">{member.source}</Badge>
              )}
              {member.created_at && (
                <span className="text-xs text-muted-foreground">
                  {new Date(member.created_at).toLocaleDateString()}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove member"
                onClick={() => onRemove(member)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const PortalUsersSection = ({
  users,
  staffSuffix,
  onInvite,
  onRemove,
}: {
  users: AgencyPortalUser[];
  staffSuffix: Agency["staff_alias_suffix"];
  onInvite: () => void;
  onRemove: (user: AgencyPortalUser) => void;
}) => (
  <section>
    <div className="mb-1 flex items-center justify-between">
      <h3 className="text-sm font-medium">Portal users</h3>
      <Button size="sm" variant="outline" onClick={onInvite}>
        <Plus className="mr-1 h-4 w-4" /> Invite
      </Button>
    </div>
    <p className="mb-2 text-xs text-muted-foreground">
      External logins for the agency portal (magic-link sign-in). They manage
      this agency&apos;s brand only — they are not CRM users. Echoflicks staff
      logins are created automatically and hidden from the agency.
    </p>
    <StaffSignIn suffix={staffSuffix} users={users} />
    {users.length === 0 ? (
      <p className="text-sm text-muted-foreground">No portal users yet.</p>
    ) : (
      <ul className="flex flex-col">
        {clientUsersFirst(users).map((user) => (
          <li
            key={user.id}
            className="flex items-center gap-2 border-b py-2 text-sm last:border-b-0"
          >
            <span>{user.email}</span>
            <Badge variant="outline">{user.role}</Badge>
            {isStaffPortalUser(user) && (
              <Badge
                variant="secondary"
                title={
                  isShadowPortalUser(user)
                    ? "Created automatically for every agency; removed when this person's staff access is revoked."
                    : "Echoflicks staff login: the agency's Team page does not list it."
                }
              >
                Echoflicks staff (hidden from client)
              </Badge>
            )}
            <span className="ml-auto flex items-center gap-1">
              <span className="text-xs text-muted-foreground">
                {user.last_login_at
                  ? `last sign-in ${new Date(user.last_login_at).toLocaleDateString()}`
                  : "never signed in"}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove portal user"
                onClick={() => onRemove(user)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

/**
 * M25-B3: the suffix list people can find. Staff sign in to this agency's
 * portal as `<name>+<suffix>@echoflicks.com` (the address reaches their normal
 * inbox); the badges are the staff logins that exist here right now.
 */
const StaffSignIn = ({
  suffix,
  users,
}: {
  suffix: Agency["staff_alias_suffix"];
  users: AgencyPortalUser[];
}) => {
  if (suffix === undefined) {
    // Row read before 20260925170000_agency_staff_shadow_accounts is applied.
    return null;
  }
  if (!suffix) {
    return (
      <p className="mb-2 text-xs text-muted-foreground">
        No staff sign-in suffix yet: staff logins have not been provisioned for
        this agency.
      </p>
    );
  }
  const emails = staffSignInEmails(users, suffix);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1 text-xs">
      <span className="text-muted-foreground">Staff sign-in:</span>
      <span className="font-mono">{staffSignInPattern(suffix)}</span>
      {emails.map((email) => (
        <Badge key={email} variant="outline" className="font-mono text-xs">
          {email}
        </Badge>
      ))}
    </div>
  );
};

const BrandSection = ({
  profile,
  onEdit,
}: {
  profile?: AgencyBrandProfile;
  onEdit: () => void;
}) => {
  const parsed = profile ? safeParseBrandProfile(profile.brand) : null;
  const brand = parsed && parsed.ok ? parsed.profile : null;
  return (
    <section>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-medium">Brand guidelines</h3>
        <Button size="sm" variant="outline" onClick={onEdit}>
          {profile ? "Edit brand" : "Add brand"}
        </Button>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Consumed by the video pipeline (closing-card agency name now; accent /
        logo behind the product-gated branded card). Changes apply to the
        member&apos;s next echo.
      </p>
      {!profile ? (
        <p className="text-sm text-muted-foreground">
          No brand profile yet — echoes render unbranded.
        </p>
      ) : parsed && !parsed.ok ? (
        <Badge variant="destructive">
          invalid profile — the bot ignores it
        </Badge>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {brand?.identity?.display_name && (
            <Badge variant="secondary">{brand.identity.display_name}</Badge>
          )}
          {brand?.palette?.primary && <ColorChip hex={brand.palette.primary} />}
          {brand?.palette?.accent && <ColorChip hex={brand.palette.accent} />}
          {brand?.contact?.agent_name && (
            <Badge variant="outline">{brand.contact.agent_name}</Badge>
          )}
          {brand?.contact?.phone_display && (
            <Badge variant="outline" className="font-mono">
              {brand.contact.phone_display}
            </Badge>
          )}
          {(brand?.tone?.adjectives ?? []).map((adjective) => (
            <Badge key={adjective} variant="outline">
              {adjective}
            </Badge>
          ))}
          {brand?.logo?.storage_path && (
            <Badge variant="outline" className="font-mono text-xs">
              logo: {brand.logo.kind}
            </Badge>
          )}
          <Badge variant="outline">
            Narrator: {brand?.prefs?.narrator ?? "Felix chooses"}
          </Badge>
          <Badge
            variant="outline"
            title="Set by the agency in the portal (Brand); the bot honours “off” only for entitled agencies (PT-050)."
          >
            Powered by Echoflicks: {effectivePoweredBy(brand)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            v{profile.version}
            {profile.updated_at &&
              ` · ${new Date(profile.updated_at).toLocaleString()}`}
          </span>
        </div>
      )}
    </section>
  );
};

const ColorChip = ({ hex }: { hex: string }) => (
  <span className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs">
    <span
      className="inline-block h-3 w-3 rounded-sm border"
      style={{ backgroundColor: hex }}
    />
    <span className="font-mono">{hex}</span>
  </span>
);

const ExtractionsSection = ({
  extractions,
}: {
  extractions: BrandExtraction[];
}) => {
  if (extractions.length === 0) {
    return null;
  }
  return (
    <section>
      <Separator className="mb-3" />
      <h3 className="mb-1 text-sm font-medium">Website extractions</h3>
      <p className="mb-2 text-xs text-muted-foreground">
        Brand-extraction attempts run from the portal&apos;s onboarding
        (suggestions only — nothing is saved without a human confirming).
      </p>
      <ul className="flex flex-col">
        {extractions.map((extraction) => (
          <li
            key={extraction.id}
            className="flex items-center gap-2 border-b py-2 text-sm last:border-b-0"
          >
            <span className="truncate font-mono text-xs">
              {extraction.final_url ?? extraction.url}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <Badge
                variant={
                  extraction.status === "succeeded"
                    ? "secondary"
                    : extraction.status === "failed"
                      ? "destructive"
                      : "outline"
                }
              >
                {extraction.status}
              </Badge>
              {typeof extraction.duration_ms === "number" && (
                <span className="text-xs text-muted-foreground">
                  {(extraction.duration_ms / 1000).toFixed(1)}s
                </span>
              )}
              {extraction.created_at && (
                <span className="text-xs text-muted-foreground">
                  {new Date(extraction.created_at).toLocaleString()}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const SetupNotice = () => (
  <div className="p-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Agencies is not provisioned yet
        </CardTitle>
        <CardDescription>
          The agency tables were not found in Supabase. Apply the migration{" "}
          <code className="font-mono">
            supabase/migrations/20260610230000_agency_brand_tenants.sql
          </code>{" "}
          to this project (SQL editor or <code>supabase db push</code>; it
          requires the feature_cohorts migration first), then reload. The deploy
          workflow cannot apply it automatically while the repo has no Supabase
          secrets.
        </CardDescription>
      </CardHeader>
    </Card>
  </div>
);
